import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Badge from 'shared/components/UC/Badge';
import type { BadgeProps } from 'shared/components/UC/Badge';
import Button from 'shared/components/UC/Button';
import {
  ApplicationWithAdminData,
  CoverageLabel,
  EvalBoardRow,
  ProgressLabel,
  ResultLabel,
} from '../../types/admin';
import adminService from '../../services/adminService';
import EvaluatorAssignmentModal from './EvaluatorAssignmentModal';
import GroupedMultiSelect from './GroupedMultiSelect';
import RecordarAppModal from './RecordarAppModal';
import styles from './EvalByApplication.module.css';

interface EvalByApplicationProps {
  rows: EvalBoardRow[];
  isLoading: boolean;
  onViewEvaluations: (applicationId: string) => void;
  onToast?: (msg: string) => void;
  /** Contenido del resumen (prosa) que se agrupa con el filterBar en la tarjeta. */
  toolsHeader?: React.ReactNode;
}

// ── Label maps ───────────────────────────────────────────────────────────────
const COVERAGE_LABELS: Record<CoverageLabel, string> = {
  none: 'Sin evaluador',
  assigned: 'Sin invitar',
  invited: 'Con invitación',
  incomplete: 'Parcial',
  complete: 'Completa',
};

const PROGRESS_LABELS: Record<ProgressLabel, string> = {
  not_started: 'Sin iniciar',
  in_progress: 'Evaluando',
  overdue: 'Atrasada',
  in_arbitration: 'En arbitraje',
  received: 'Completo',
};

const RESULT_LABELS: Record<ResultLabel, string> = {
  no_deviation: 'Sin desviación',
  requires_arbitration: 'Requiere árbitro',
  arbiter_invited: 'Árbitro invitado',
  arbiter_accepted: 'Árbitro sin iniciar',
  arbitration_in_progress: 'Arbitraje en curso',
  arbitration_resolved: 'Arbitraje finalizado',
  resolved: 'Sin controversia',
};

// ── Badge variant maps ────────────────────────────────────────────────────────
const COVERAGE_VARIANT: Record<CoverageLabel, BadgeProps['variant']> = {
  none:       'warning',
  assigned:   'warning',
  invited:    'info',
  incomplete: 'warning',
  complete:   'success',
};

const PROGRESS_VARIANT: Record<ProgressLabel, BadgeProps['variant']> = {
  not_started:     'secondary',
  in_progress:     'info',
  overdue:         'danger',
  in_arbitration:  'warning',
  received:        'success',
};

const RESULT_VARIANT: Record<ResultLabel, BadgeProps['variant']> = {
  no_deviation:            'secondary',
  requires_arbitration:    'warning',
  arbiter_invited:         'info',
  arbiter_accepted:        'info',
  arbitration_in_progress: 'info',
  arbitration_resolved:    'success',
  resolved:                'success',
};


const PROGRESS_ORDER: Record<ProgressLabel, number> = {
  overdue: 0,
  in_progress: 1,
  in_arbitration: 2,
  not_started: 3,
  received: 4,
};

// ── Options for GroupedMultiSelect ────────────────────────────────────────────
const COVERAGE_OPTIONS = Object.entries(COVERAGE_LABELS).map(([value, label]) => ({ value, label }));
const PROGRESS_OPTIONS = Object.entries(PROGRESS_LABELS).map(([value, label]) => ({ value, label }));
// Exclude 'no_deviation' — it's the default state and not actionable as a filter
const RESULT_OPTIONS = (Object.entries(RESULT_LABELS) as [ResultLabel, string][])
  .filter(([value]) => value !== 'no_deviation')
  .map(([value, label]) => ({ value, label }));
const RECHAZOS_OPTIONS = [
  { value: 'rechazada', label: 'Excusada' },
  { value: 'conflicto', label: 'Conflicto declarado' },
  { value: 'pendiente', label: 'Pendiente' },
  { value: 'aceptada', label: 'Aceptada' },
];

// ── Component ─────────────────────────────────────────────────────────────────
const EvalByApplication: React.FC<EvalByApplicationProps> = ({
  rows,
  isLoading,
  onViewEvaluations,
  onToast,
  toolsHeader,
}) => {
  const navigate = useNavigate();
  const [filterCoverage, setFilterCoverage] = useState<string[]>([]);
  const [filterProgress, setFilterProgress] = useState<string[]>([]);
  const [filterResult, setFilterResult] = useState<string[]>([]);
  const [filterBajas, setFilterBajas] = useState<string[]>([]);
  const [sortCol, setSortCol] = useState<string>('applicationNumber');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [recordarModal, setRecordarModal] = useState<EvalBoardRow | null>(null);
  const [assignModal, setAssignModal] = useState<ApplicationWithAdminData | null>(null);
  const [assignLoading, setAssignLoading] = useState<string | null>(null); // applicationId while loading

  const handleAssignClick = async (applicationId: string) => {
    setAssignLoading(applicationId);
    try {
      const app = await adminService.getApplicationById(applicationId);
      setAssignModal(app);
    } catch {
      onToast?.('Error al cargar la postulación');
    } finally {
      setAssignLoading(null);
    }
  };

  const toggleSort = (col: string) => {
    setSortDir((d) => (sortCol === col && d === 'asc' ? 'desc' : 'asc'));
    setSortCol(col);
  };

  const sortIconClass = (col: string) =>
    sortCol === col ? styles.sortIconActive : styles.sortIcon;

  const sortIconName = (col: string) =>
    sortCol === col && sortDir === 'asc' ? 'arrow_upward' : 'arrow_downward';

  // Filter — alert filter acts as a preset, facet filters are applied on top
  const filtered = useMemo(() => {
    let r = rows;
    if (filterCoverage.length > 0)
      r = r.filter((row) => filterCoverage.includes(row.coverage));
    if (filterProgress.length > 0)
      r = r.filter((row) => filterProgress.includes(row.progress));
    if (filterResult.length > 0)
      r = r.filter((row) => filterResult.includes(row.result));
    if (filterBajas.length > 0) {
      r = r.filter((row) => {
        const dec = row.assignments.filter((a) => a.status === 'declined').length;
        const con = row.assignments.filter((a) => a.status === 'conflict_declared').length;
        const acc = row.assignments.filter((a) =>
          a.status === 'accepted_not_started' || a.status === 'in_progress' || a.status === 'completed'
        ).length;
        const pend = row.assignments.filter((a) => a.status === 'pending').length;
        return filterBajas.some((f) =>
          f === 'rechazada' ? dec > 0
          : f === 'conflicto' ? con > 0
          : f === 'aceptada' ? acc > 0
          : f === 'pendiente' ? pend > 0
          : false
        );
      });
    }
    return r;
  }, [rows, filterCoverage, filterProgress, filterResult, filterBajas]);

  // Sort
  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      let av: number | string;
      let bv: number | string;
      switch (sortCol) {
        case 'applicationNumber':
          av = a.applicationNumber;
          bv = b.applicationNumber;
          break;
        case 'daysOverdue': {
          const po = PROGRESS_ORDER[a.progress] - PROGRESS_ORDER[b.progress];
          if (po !== 0) return sortDir === 'asc' ? po : -po;
          // Within same progress label, sort overdue items by days
          return sortDir === 'asc'
            ? b.daysOverdue - a.daysOverdue
            : a.daysOverdue - b.daysOverdue;
        }
        case 'maxDeviation':
          av = a.maxDeviation ?? -1;
          bv = b.maxDeviation ?? -1;
          break;
        case 'avgScore':
          av = a.avgScore ?? -1;
          bv = b.avgScore ?? -1;
          break;
        default:
          av = a.applicationNumber;
          bv = b.applicationNumber;
      }
      if (typeof av === 'number' && typeof bv === 'number') {
        return sortDir === 'asc' ? av - bv : bv - av;
      }
      return sortDir === 'asc'
        ? String(av).localeCompare(String(bv), 'es')
        : String(bv).localeCompare(String(av), 'es');
    });
  }, [filtered, sortCol, sortDir]);

  // Options with a count per value, e.g. "Sin evaluador (8)". Counts are over
  // the full board (rows), independent of other selected facets, and mirror the
  // predicates used in `filtered` so each number matches what the filter shows.
  const coverageOptions = useMemo(() => {
    const counts: Record<string, number> = {};
    rows.forEach((row) => {
      counts[row.coverage] = (counts[row.coverage] ?? 0) + 1;
    });
    return COVERAGE_OPTIONS.map((o) => ({
      ...o,
      label: `${o.label} (${counts[o.value] ?? 0})`,
    }));
  }, [rows]);

  const progressOptions = useMemo(() => {
    const counts: Record<string, number> = {};
    rows.forEach((row) => {
      counts[row.progress] = (counts[row.progress] ?? 0) + 1;
    });
    return PROGRESS_OPTIONS.map((o) => ({
      ...o,
      label: `${o.label} (${counts[o.value] ?? 0})`,
    }));
  }, [rows]);

  const resultOptions = useMemo(() => {
    const counts: Record<string, number> = {};
    rows.forEach((row) => {
      counts[row.result] = (counts[row.result] ?? 0) + 1;
    });
    return RESULT_OPTIONS.map((o) => ({
      ...o,
      label: `${o.label} (${counts[o.value] ?? 0})`,
    }));
  }, [rows]);

  const bajasOptions = useMemo(() => {
    const has = (row: EvalBoardRow, statuses: string[]) =>
      row.assignments.some((a) => statuses.includes(a.status));
    const counts: Record<string, number> = {
      rechazada: rows.filter((row) => has(row, ['declined'])).length,
      conflicto: rows.filter((row) => has(row, ['conflict_declared'])).length,
      aceptada: rows.filter((row) =>
        has(row, ['accepted_not_started', 'in_progress', 'completed'])
      ).length,
      pendiente: rows.filter((row) => has(row, ['pending'])).length,
    };
    return RECHAZOS_OPTIONS.map((o) => ({
      ...o,
      label: `${o.label} (${counts[o.value] ?? 0})`,
    }));
  }, [rows]);

  const handleDownloadCSV = () => {
    const esc = (v: string | number | null | undefined) => {
      if (v === null || v === undefined) return '';
      const s = String(v);
      return s.includes(',') || s.includes('"') || s.includes('\n')
        ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const headers = [
      'Folio', 'Proyecto', 'IR', 'Unidad académica',
      'Asignación', 'Respuesta - Pendiente', 'Respuesta - Aceptada', 'Respuesta - Excusada', 'Respuesta - Conflicto declarado',
      'Progreso', 'Días de atraso', 'Resultado', 'Score promedio',
    ];
    const csvRows = [
      headers.join(','),
      ...sorted.map((r) => {
        const excusadas = r.assignments.filter((a) => a.status === 'declined').length;
        const conflictos = r.assignments.filter((a) => a.status === 'conflict_declared').length;
        const pendientes = r.assignments.filter((a) => a.status === 'pending').length;
        const aceptadas = r.assignments.filter((a) =>
          ['accepted', 'in_progress', 'completed'].includes(a.status)
        ).length;
        return [
          esc(r.applicationNumber),
          esc(r.title),
          esc(r.irName),
          esc(r.unidadAcademica),
          esc(COVERAGE_LABELS[r.coverage]),
          pendientes,
          aceptadas,
          excusadas,
          conflictos,
          esc(PROGRESS_LABELS[r.progress]),
          r.daysOverdue > 0 ? r.daysOverdue : '',
          esc(RESULT_LABELS[r.result]),
          r.avgScore !== null ? r.avgScore.toFixed(2) : '',
        ].join(',');
      }),
    ];
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'evaluaciones_por_postulacion.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return (
      <div className={styles.wrapper}>
        {toolsHeader && <div className={styles.toolsCard}>{toolsHeader}</div>}
        <div className={styles.loading}>
          <div className={styles.spinner} aria-hidden="true" />
          <span>Cargando tablero de evaluaciones...</span>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      {/* Tarjeta de herramientas: KPIs (slot) + banner + filtros agrupados;
          la tabla queda afuera, abajo. */}
      <div className={styles.toolsCard}>
      {toolsHeader}
      {/* Filter bar */}
      <div className={styles.filterBar}>
        <GroupedMultiSelect
          groups={[{ options: coverageOptions }]}
          selected={filterCoverage}
          onChange={setFilterCoverage}
          placeholder="Asignación de Evaluadores"
        />
        <GroupedMultiSelect
          groups={[{ options: bajasOptions }]}
          selected={filterBajas}
          onChange={setFilterBajas}
          placeholder="Respuesta a invitación"
        />
        <GroupedMultiSelect
          groups={[{ options: progressOptions }]}
          selected={filterProgress}
          onChange={setFilterProgress}
          placeholder="Progreso"
        />
        <GroupedMultiSelect
          groups={[{ options: resultOptions }]}
          selected={filterResult}
          onChange={setFilterResult}
          placeholder="Arbitraje"
        />
        <Button variant="primary" size="sm" onClick={handleDownloadCSV}>
          Descargar CSV
        </Button>
      </div>
      </div>

      {sorted.length === 0 ? (
        <div className={styles.empty}>
          {rows.length === 0
            ? 'No hay postulaciones en evaluación para este concurso.'
            : 'Ninguna postulación coincide con los filtros seleccionados.'}
        </div>
      ) : (
        <div className={styles.tableContainer}>
          <table className={styles.table} aria-label="Tablero de control de evaluaciones">
            <thead>
              <tr>
                <th
                  className={`${styles.sortableHeader}`}
                  onClick={() => toggleSort('applicationNumber')}
                  aria-sort={
                    sortCol === 'applicationNumber'
                      ? sortDir === 'asc'
                        ? 'ascending'
                        : 'descending'
                      : 'none'
                  }
                >
                  <span className={styles.sortHeaderContent}>
                    Folio
                    <i className={`uc-icon icon-size--sm ${sortIconClass('applicationNumber')}`}>
                      {sortIconName('applicationNumber')}
                    </i>
                  </span>
                </th>
                <th>Proyecto / IR</th>
                <th>
                  <span className={styles.headerWithTooltip}>
                    Asignación
                    <span className={styles.helpTooltip}>
                      ?
                      <span className={styles.tooltipContent} role="tooltip">
                        <strong>Sin evaluador</strong> — ningún evaluador activo; requiere asignación urgente.<br />
                        <strong>Sin invitar</strong> — hay evaluadores asignados, pero todavía no se les envió la invitación.<br />
                        <strong>Con invitación</strong> — hay invitaciones enviadas pendientes de respuesta, pero aún sin evaluadores activos.<br />
                        <strong>Parcial</strong> — hay evaluadores activos pero aún no se alcanza el cupo del concurso.<br />
                        <strong>Completa</strong> — se ha alcanzado el cupo de evaluadores requerido.
                      </span>
                    </span>
                  </span>
                </th>
                <th>Respuesta a invitación</th>
                <th
                  className={styles.sortableHeader}
                  onClick={() => toggleSort('daysOverdue')}
                  aria-sort={
                    sortCol === 'daysOverdue'
                      ? sortDir === 'asc'
                        ? 'ascending'
                        : 'descending'
                      : 'none'
                  }
                >
                  <span className={styles.sortHeaderContent}>
                    Progreso
                    <i className={`uc-icon icon-size--sm ${sortIconClass('daysOverdue')}`}>
                      {sortIconName('daysOverdue')}
                    </i>
                  </span>
                </th>
                <th>Puntajes</th>
                <th
                  className={styles.sortableHeader}
                  onClick={() => toggleSort('avgScore')}
                  aria-sort={
                    sortCol === 'avgScore'
                      ? sortDir === 'asc'
                        ? 'ascending'
                        : 'descending'
                      : 'none'
                  }
                >
                  <span className={styles.sortHeaderContent}>
                    Promedio
                    <i className={`uc-icon icon-size--sm ${sortIconClass('avgScore')}`}>
                      {sortIconName('avgScore')}
                    </i>
                  </span>
                </th>
                <th
                  className={styles.sortableHeader}
                  onClick={() => toggleSort('maxDeviation')}
                  aria-sort={
                    sortCol === 'maxDeviation'
                      ? sortDir === 'asc'
                        ? 'ascending'
                        : 'descending'
                      : 'none'
                  }
                >
                  <span className={styles.sortHeaderContent}>
                    Desviación
                    <i className={`uc-icon icon-size--sm ${sortIconClass('maxDeviation')}`}>
                      {sortIconName('maxDeviation')}
                    </i>
                  </span>
                </th>
                <th>Acción</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((row) => (
                <tr key={row.applicationId} className={styles.row}>
                  {/* Folio */}
                  <td className={styles.folio}>
                    <div className={styles.folioCell}>
                      <button
                        className={styles.folioLink}
                        onClick={() => navigate(`/admin/application/${row.applicationId}`)}
                      >
                        {row.applicationNumber}
                      </button>
                      <button
                        className={styles.folioNewTab}
                        onClick={() => window.open(`/admin/application/${row.applicationId}`, '_blank')}
                        aria-label="Abrir en pestaña nueva"
                        title="Abrir en pestaña nueva"
                      >
                        <i className="uc-icon icon-size--sm" aria-hidden="true">open_in_new</i>
                      </button>
                    </div>
                  </td>

                  {/* Proyecto */}
                  <td>
                    <div className={styles.projectTitle} title={row.title}>
                      {row.title}
                    </div>
                    <div className={styles.projectMeta} title={`${row.irName} · ${row.unidadAcademica}`}>
                      {row.irName}
                      {row.unidadAcademica !== '—' && ` · ${row.unidadAcademica}`}
                    </div>
                  </td>

                  {/* Cobertura */}
                  <td>
                    <Badge label={COVERAGE_LABELS[row.coverage]} variant={COVERAGE_VARIANT[row.coverage]} />
                  </td>

                  {/* Bajas */}
                  <td>
                    {(() => {
                      const declinedCount = row.assignments.filter((a) => a.status === 'declined').length;
                      const conflictCount = row.assignments.filter((a) => a.status === 'conflict_declared').length;
                      const pendingCount = row.assignments.filter((a) => a.status === 'pending').length;
                      const acceptedCount = row.assignments.filter((a) =>
                        ['accepted', 'in_progress', 'completed'].includes(a.status)
                      ).length;
                      if (declinedCount === 0 && conflictCount === 0 && pendingCount === 0 && acceptedCount === 0) {
                        return <span className={styles.muted}>—</span>;
                      }
                      return (
                        <div className={styles.optOutBadges}>
                          {pendingCount > 0 && (
                            <Badge label={pendingCount > 1 ? `Pendiente ×${pendingCount}` : 'Pendiente'} variant="info" />
                          )}
                          {acceptedCount > 0 && (
                            <Badge label={acceptedCount > 1 ? `Aceptada ×${acceptedCount}` : 'Aceptada'} variant="success" />
                          )}
                          {declinedCount > 0 && (() => {
                            const DECLINE_LABELS: Record<string, string> = {
                              thematic: 'Área no afín',
                              conflict: 'Conflicto de interés',
                              other: 'Otro motivo',
                            };
                            const declinedAssignments = row.assignments.filter((a) => a.status === 'declined');
                            const tooltipLines = declinedAssignments
                              .filter((a) => a.declineReason || a.declineNote)
                              .map((a) => {
                                const label = a.declineReason ? DECLINE_LABELS[a.declineReason] ?? a.declineReason : '';
                                return a.declineNote ? `${a.evaluatorName}: ${label} — ${a.declineNote}` : `${a.evaluatorName}: ${label}`;
                              });
                            return (
                              <span className={styles.declinedBadgeWrapper}>
                                <Badge label={declinedCount > 1 ? `Excusada ×${declinedCount}` : 'Excusada'} variant="warning" />
                                {tooltipLines.length > 0 && (
                                  <span className={styles.helpTooltip} tabIndex={0}>
                                    i
                                    <span className={styles.tooltipContent}>
                                      {tooltipLines.map((line, i) => (
                                        <span key={i} style={{ display: 'block' }}>{line}</span>
                                      ))}
                                    </span>
                                  </span>
                                )}
                              </span>
                            );
                          })()}
                          {conflictCount > 0 && (
                            <Badge label={conflictCount > 1 ? `Conflicto declarado ×${conflictCount}` : 'Conflicto declarado'} variant="warning" />
                          )}
                        </div>
                      );
                    })()}
                  </td>

                  {/* Progreso */}
                  <td>
                    <Badge label={PROGRESS_LABELS[row.progress]} variant={PROGRESS_VARIANT[row.progress]} />
                    {row.progress === 'overdue' && row.daysOverdue > 0 && (
                      <span className={styles.overdueDays}>
                        {row.daysOverdue} {row.daysOverdue === 1 ? 'día' : 'días'}
                      </span>
                    )}
                  </td>

                  {/* Puntajes */}
                  <td>
                    {row.completedScores.length === 0 ? (
                      <span className={styles.muted}>—</span>
                    ) : (
                      <div className={styles.scoreChips}>
                        {row.completedScores.map((score, i) => (
                          <span key={`${i}-${score}`} className={styles.scoreChip}>
                            E{i + 1}: {score.toFixed(1)}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>

                  {/* Promedio */}
                  <td>
                    {row.avgScore !== null ? (
                      <span className={styles.avgScore}>{row.avgScore.toFixed(1)}</span>
                    ) : (
                      <span className={styles.muted}>—</span>
                    )}
                  </td>

                  {/* Desviación */}
                  <td>
                    {row.maxDeviation !== null ? (
                      <>
                        <div className={styles.deviationValue}>{row.maxDeviation.toFixed(2)}</div>
                        {row.result !== 'no_deviation' && row.result !== 'resolved' && (
                          <Badge label={RESULT_LABELS[row.result]} variant={RESULT_VARIANT[row.result]} />
                        )}
                      </>
                    ) : (
                      <span className={styles.muted}>—</span>
                    )}
                  </td>

                  {/* Acción — prioridad: Reemplazos > Asignar > Recordar > Ver evaluaciones > — */}
                  <td className={styles.actionCell}>
                    {(() => {
                      const optOuts = row.assignments.filter(
                        (a) => a.status === 'declined' || a.status === 'conflict_declared'
                      );
                      if (optOuts.length > 0 && row.coverage !== 'complete') {
                        return (
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => handleAssignClick(row.applicationId)}
                          >
                            Reemplazos
                          </Button>
                        );
                      }
                      const needsAssignment =
                        (row.coverage === 'none' || row.coverage === 'incomplete') &&
                        optOuts.length === 0;
                      if (needsAssignment) {
                        const isLoading = assignLoading === row.applicationId;
                        return (
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => handleAssignClick(row.applicationId)}
                            disabled={isLoading}
                          >
                            {isLoading ? '...' : 'Asignar'}
                          </Button>
                        );
                      }
                      const reminderTargets = row.assignments.filter((a) =>
                        ['pending', 'accepted_not_started', 'in_progress'].includes(a.status)
                      );
                      if (row.progress === 'overdue' && reminderTargets.length > 0) {
                        return (
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => setRecordarModal(row)}
                          >
                            Recordar
                          </Button>
                        );
                      }
                      if (
                        row.completedScores.length > 0 &&
                        (row.progress === 'received' || row.progress === 'in_arbitration')
                      ) {
                        return (
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => onViewEvaluations(row.applicationId)}
                          >
                            Ver evaluaciones
                          </Button>
                        );
                      }
                      return <span className={styles.muted}>—</span>;
                    })()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <RecordarAppModal
        isOpen={recordarModal !== null}
        applicationNumber={recordarModal?.applicationNumber ?? ''}
        title={recordarModal?.title ?? ''}
        targets={
          recordarModal?.assignments.filter((a) =>
            ['pending', 'accepted_not_started', 'in_progress'].includes(a.status)
          ) ?? []
        }
        onClose={() => setRecordarModal(null)}
        onSent={(count) => {
          onToast?.(`Recordatorio enviado a ${count} evaluador${count !== 1 ? 'es' : ''}.`);
          setRecordarModal(null);
        }}
      />

      {assignModal && (
        <EvaluatorAssignmentModal
          application={assignModal}
          isOpen={true}
          onClose={() => setAssignModal(null)}
          onAssignmentComplete={() => {
            onToast?.('Evaluador asignado correctamente.');
            setAssignModal(null);
          }}
        />
      )}
    </div>
  );
};

export default EvalByApplication;
